import { forwardRef, useImperativeHandle, useRef } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useFieldAria } from "./field-context";

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
  /** When true, render a small X icon at the trailing edge that calls
   *  `onClear` (or fires a synthetic onChange with an empty value). The
   *  icon only appears when the input has a non-empty value and isn't
   *  disabled / read-only — staff can wipe out a field without
   *  triple-clicking + delete. Mirrors the browser's native search-input
   *  affordance but works on every input type and ours is keyboard-focusable. */
  clearable?: boolean;
  /** Called when the trailing X is clicked. If absent, we fire a synthetic
   *  onChange event with value="" so plain `register()`-style usages still
   *  work. */
  onClear?: () => void;
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      invalid,
      type = "text",
      clearable,
      onClear,
      value,
      defaultValue,
      readOnly,
      disabled,
      ...props
    },
    forwardedRef,
  ) => {
    const localRef = useRef<HTMLInputElement | null>(null);
    useImperativeHandle(forwardedRef, () => localRef.current as HTMLInputElement);

    // Pick up aria-describedby + aria-invalid from the surrounding
    // <Field>. Caller-supplied props win — usually a date/time/address
    // field that already wires its own describedby.
    const { describedBy: ctxDescribedBy, invalid: ctxInvalid } = useFieldAria();
    const explicitDescribedBy = props["aria-describedby"];
    const ariaDescribedBy = explicitDescribedBy ?? ctxDescribedBy;
    const ariaInvalid = invalid ?? ctxInvalid;

    // For uncontrolled inputs we rely on the live DOM node to know whether
    // the value is empty — controlled inputs already pass `value` in.
    const isControlled = value !== undefined;
    const hasValue = isControlled
      ? typeof value === "string"
        ? value.length > 0
        : value != null && String(value).length > 0
      : Boolean(localRef.current?.value);

    const showClear = clearable && hasValue && !disabled && !readOnly;

    const inputEl = (
      <input
        ref={localRef}
        type={type}
        aria-invalid={ariaInvalid || undefined}
        aria-describedby={ariaDescribedBy}
        value={value}
        defaultValue={defaultValue}
        readOnly={readOnly}
        disabled={disabled}
        className={cn(
          "h-10 w-full rounded-xl border border-border bg-foreground/[0.03] px-3 text-sm text-foreground transition placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-0 disabled:opacity-60",
          // Reserve a strip on the right when clearable so the X never
          // overlaps user-typed text mid-input.
          showClear && "pr-9",
          (invalid || ariaInvalid) && "border-destructive focus:ring-destructive",
          className,
        )}
        {...props}
      />
    );

    if (!clearable) return inputEl;

    return (
      <div className="relative inline-flex w-full">
        {inputEl}
        {showClear ? (
          <button
            type="button"
            tabIndex={-1}
            aria-label="Clear value"
            onMouseDown={(e) => {
              // Prevent input blur so the focus-ring stays visible while
              // we mutate the value — feels more responsive than
              // clear-then-refocus.
              e.preventDefault();
            }}
            onClick={() => {
              if (onClear) {
                onClear();
              } else if (localRef.current) {
                // Fall back to a synthetic input event so RHF's `register()`
                // path still picks up the change. Using the native setter
                // ensures React's internal value tracker sees the update.
                const setter = Object.getOwnPropertyDescriptor(
                  window.HTMLInputElement.prototype,
                  "value",
                )?.set;
                setter?.call(localRef.current, "");
                localRef.current.dispatchEvent(new Event("input", { bubbles: true }));
              }
              localRef.current?.focus();
            }}
            className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted-foreground transition hover:text-destructive focus:outline-none focus-visible:text-destructive"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.4} />
          </button>
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";
